import { map } from 'lodash-es';

import { ModuleLoaderValue, ModuleState } from 'hooks/loader/ModuleLoaderProvider';
import { services } from 'services/base';
import { Json, ModuleMeta, PluginModule, PluginModuleConfig } from 'types';

import { convertExceptionToJson } from './helpers';
import logger from './logger';

export interface PluginModuleConfigWithKey extends PluginModuleConfig {
  key: string;
}

export type PluginModuleMethod = keyof Required<PluginModule>;
export type PluginLoadMethodName = 'load' | PluginModuleMethod;

export type PluginModuleLoadBase = {
  method: PluginLoadMethodName;
  meta: Omit<ModuleMeta, 'moduleConfig'> | null;
};
export type PluginModuleLoadError = PluginModuleLoadBase & {
  error: Json;
};
export type PluginModuleLoadResult = PluginModuleLoadBase | PluginModuleLoadError;

export type LoadPluginsParam = {
  pluginsToLoad: PluginModuleConfigWithKey[];
  methods: PluginModuleMethod[];
  loadModule: ModuleLoaderValue['load'];
  getModulesMap: ModuleLoaderValue['getModulesMap'];
};

const invokeModuleLifecycleMethod =
  (method: PluginModuleMethod) =>
  async ({ module, error: moduleLoadError, meta }: ModuleState): Promise<PluginModuleLoadResult> => {
    try {
      if (module && !moduleLoadError) {
        if (module[method]) {
          await module[method](services, meta);
        } else if (method === 'prelogin') {
          logger.warn('Module defined with prelogin functionality, but no `prelogin` method found!');
        }
      }
      return {
        method,
        meta: JSON.parse(JSON.stringify(meta)),
      };
    } catch (error: unknown) {
      logger.error('Error invoking plugin module method!', { method, meta, error });

      return {
        method,
        meta: JSON.parse(JSON.stringify(meta)),
        error: convertExceptionToJson(error),
      };
    }
  };

const isLoadError = (result: PluginModuleLoadResult): result is PluginModuleLoadError =>
  !!(result as PluginModuleLoadError).error;

const loadPlugins = async ({ pluginsToLoad, methods, loadModule, getModulesMap }: LoadPluginsParam) => {
  const pluginsLoadPromises = pluginsToLoad.map(async (pluginConfig): Promise<PluginModuleLoadResult> => {
    const moduleId = pluginConfig.key;
    try {
      await loadModule(moduleId);
      return {
        method: 'load',
        meta: getModulesMap()[moduleId]?.meta || null,
      };
    } catch (error: unknown) {
      const meta = getModulesMap()[moduleId]?.meta || null;
      logger.error('Error occurred while loading plugin!', { meta, error });
      return {
        method: 'load',
        meta: JSON.parse(JSON.stringify(meta)),
        error: convertExceptionToJson(error),
      };
    }
  });

  const results: PluginModuleLoadResult[] = [];
  const lastResult = await methods.reduce(async (promise, method) => {
    const result = await promise;
    results.push(...result);

    const promises = map(getModulesMap(), invokeModuleLifecycleMethod(method));
    return Promise.all(promises);
  }, Promise.all(pluginsLoadPromises));
  results.push(...lastResult);

  return results.filter(isLoadError);
};

export default loadPlugins;
